// src/pages/hreval/ContractWorkerEvaluation.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import useBackdropClose from "../../hooks/useBackdropClose";
import { getAccessToken } from "../../auth";
import styles from "./ContractWorkerEvaluation.module.css";

const API_BASE  = "/api/contract-workers";
const THIS_YEAR = new Date().getFullYear();

const EMPTY_FORM = {
  name: "", gender: "남", birth: "", phone: "",
  site: "", field: "", working: "Y",
  hire_date: "", resign_date: "",
};

function makeEmptyEval(site = "") {
  const today = new Date();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  return {
    site,
    evaluator:   "",
    content:     "",
    score_type:  "minus",
    score_value: 0,
    eval_date:   `${THIS_YEAR}-${mm}-${dd}`,
  };
}

async function apiFetch(url, options = {}) {
  const token      = getAccessToken();
  const isFormData = options.body instanceof FormData;
  const res = await fetch(url, {
    ...options,
    headers: {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
  if (res.status === 204) return null;
  return res.json();
}

function formatPhone(v) {
  const d = (v || "").replace(/\D/g, "").slice(0, 11);
  if (d.length < 4)  return d;
  if (d.length < 8)  return `${d.slice(0, 3)}-${d.slice(3)}`;
  if (d.length < 11) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  return                    `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
}

function scoreColor(score) {
  if (score >= 95) return { bg: "#dcfce7", border: "#22c55e", text: "#15803d" };
  if (score >= 85) return { bg: "#dbeafe", border: "#3b82f6", text: "#1d4ed8" };
  if (score >= 75) return { bg: "#fef3c7", border: "#f59e0b", text: "#b45309" };
  if (score >= 65) return { bg: "#fed7aa", border: "#ea580c", text: "#c2410c" };
  return                   { bg: "#fee2e2", border: "#ef4444", text: "#991b1b" };
}

function changeLabel(change) {
  if (change > 0) return { text: `+${change}점`, color: "#16a34a" };
  if (change < 0) return { text: `${change}점`,  color: "#dc2626" };
  return                  { text: "±0점",         color: "#64748b" };
}

function groupEvalsByYear(evals) {
  const map = {};
  (evals || []).forEach((ev) => {
    const yr = ev.eval_date ? new Date(ev.eval_date).getFullYear() : THIS_YEAR;
    if (!map[yr]) map[yr] = [];
    map[yr].push(ev);
  });
  return Object.fromEntries(
    Object.entries(map).sort(([a], [b]) => Number(b) - Number(a))
  );
}

export default function ContractWorkerEvaluation() {
  const modalBackdrop = useBackdropClose(() => setModalOpen(false));

  const [workers,  setWorkers]  = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [searchQ,  setSearchQ]  = useState("");

  const [drawerWorker, setDrawerWorker] = useState(null);
  const [drawerOpen,   setDrawerOpen]   = useState(false);

  const [editingKey, setEditingKey] = useState(null);
  const [evalDraft,  setEvalDraft]  = useState(makeEmptyEval());

  const [openYears, setOpenYears] = useState(new Set([THIS_YEAR]));

  const [modalOpen,   setModalOpen]   = useState(false);
  const [modalEditId, setModalEditId] = useState(null);
  const [form,        setForm]        = useState(EMPTY_FORM);

  const fileInputRef   = useRef(null);
  const [uploadStatus, setUploadStatus] = useState(null);

  const clickTimer = useRef(null);

  /* ── 목록 조회 ─────────────────────────── */
  const loadWorkers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQ) params.set("search", searchQ);
      const data = await apiFetch(`${API_BASE}/?${params}`);
      setWorkers(Array.isArray(data) ? data : (data.results || []));
    } catch (e) {
      console.error(e);
      alert("목록을 불러올 수 없습니다.");
    } finally {
      setLoading(false);
    }
  }, [searchQ]);

  useEffect(() => { loadWorkers(); }, [loadWorkers]);

  useEffect(() => {
    if (drawerWorker) {
      const updated = workers.find((w) => w.id === drawerWorker.id);
      if (updated) setDrawerWorker(updated);
    }
  }, [workers]);

  /* ── 연도 컬럼 ─────────────────────────── */
  const years = useMemo(() => {
    const set = new Set([THIS_YEAR]);
    workers.forEach((w) =>
      Object.keys(w.scores_by_year || {}).forEach((y) => set.add(Number(y)))
    );
    return Array.from(set).sort((a, b) => b - a);
  }, [workers]);

  /* ── 더블클릭 드로어 ───────────────────── */
  const handleRowClick = (w) => {
    if (clickTimer.current) {
      clearTimeout(clickTimer.current);
      clickTimer.current = null;
      openDrawer(w);
    } else {
      clickTimer.current = setTimeout(() => { clickTimer.current = null; }, 260);
    }
  };

  const openDrawer = (w) => {
    setDrawerWorker(w);
    setDrawerOpen(true);
    setEditingKey(null);
    setEvalDraft(makeEmptyEval(w.site));
    setOpenYears(new Set([THIS_YEAR]));
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingKey(null);
  };

  /* ── 평가 CRUD ─────────────────────────── */
  const startAddEval = (year) => {
    const today = new Date();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    setEditingKey("new");
    setEvalDraft({
      ...makeEmptyEval(drawerWorker?.site || ""),
      eval_date: `${year}-${mm}-${dd}`,
    });
  };

  const startEditEval = (ev) => {
    const change = ev.score_change || 0;
    setEditingKey(ev.id);
    setEvalDraft({
      site:        ev.site || "",
      evaluator:   ev.evaluator || "",
      content:     ev.content || "",
      score_type:  change >= 0 ? "plus" : "minus",
      score_value: Math.abs(change),
      eval_date:   ev.eval_date || new Date().toISOString().slice(0, 10),
    });
  };

  const cancelEditEval = () => {
    setEditingKey(null);
    setEvalDraft(makeEmptyEval(drawerWorker?.site || ""));
  };

  const saveEval = async (evalId) => {
    if (!drawerWorker) return;
    const signedChange =
      evalDraft.score_type === "plus"
        ? Number(evalDraft.score_value || 0)
        : -Number(evalDraft.score_value || 0);

    const payload = {
      site:         evalDraft.site,
      evaluator:    evalDraft.evaluator,
      content:      evalDraft.content,
      score_change: signedChange,
      eval_date:    evalDraft.eval_date,
    };
    try {
      if (evalId === "new") {
        await apiFetch(`${API_BASE}/${drawerWorker.id}/evals/`, {
          method: "POST", body: JSON.stringify(payload),
        });
      } else {
        await apiFetch(`${API_BASE}/${drawerWorker.id}/evals/${evalId}/`, {
          method: "PATCH", body: JSON.stringify(payload),
        });
      }
      cancelEditEval();
      const addedYear = new Date(evalDraft.eval_date).getFullYear();
      setOpenYears((p) => new Set([...p, addedYear]));
      await loadWorkers();
    } catch (e) {
      console.error(e);
      alert("저장 실패");
    }
  };

  const deleteEval = async (evalId) => {
    if (!window.confirm("이 평가를 삭제하시겠습니까?")) return;
    try {
      await apiFetch(`${API_BASE}/${drawerWorker.id}/evals/${evalId}/`, { method: "DELETE" });
      await loadWorkers();
    } catch (e) { alert("삭제 실패"); }
  };

  /* ── 직원 CRUD ─────────────────────────── */
  const openModal = (editId = null) => {
    setModalEditId(editId);
    if (editId) {
      const w = workers.find((x) => x.id === editId);
      if (w) setForm({
        name:        w.name,
        gender:      w.gender,
        birth:       w.birth || "",
        phone:       formatPhone(w.phone || ""),
        site:        w.site,
        field:       w.field || "",
        working:     w.working,
        hire_date:   w.hire_date || "",
        resign_date: w.resign_date || "",
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setModalOpen(true);
  };

  const saveModal = async () => {
    if (!form.name.trim()) return alert("이름을 입력해주세요.");
    if (!form.site.trim()) return alert("근무지를 입력해주세요.");
    try {
      const payload = { ...form };
      // 빈 날짜는 null로
      if (!payload.hire_date)   payload.hire_date   = null;
      if (!payload.resign_date) payload.resign_date = null;
      if (!payload.birth)       payload.birth       = null;

      if (modalEditId) {
        await apiFetch(`${API_BASE}/${modalEditId}/`, { method: "PATCH", body: JSON.stringify(payload) });
      } else {
        await apiFetch(`${API_BASE}/`, { method: "POST", body: JSON.stringify(payload) });
      }
      setModalOpen(false);
      await loadWorkers();
    } catch (e) { alert("저장 실패"); }
  };

  const deleteWorker = async (id) => {
    if (!window.confirm("삭제하시겠습니까?\n(평가 이력도 함께 삭제됩니다)")) return;
    try {
      await apiFetch(`${API_BASE}/${id}/`, { method: "DELETE" });
      if (drawerWorker?.id === id) closeDrawer();
      await loadWorkers();
    } catch (e) { alert("삭제 실패"); }
  };

  /* ── 엑셀 업로드 ───────────────────────── */
  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      setUploadStatus({ type: "loading", msg: "업로드 중..." });
      const r = await apiFetch(`${API_BASE}/upload-excel/`, { method: "POST", body: fd });
      setUploadStatus({
        type: "ok",
        msg:  `완료: 신규 ${r.created_workers}명 / 수정 ${r.updated_workers}명 / 평가 ${r.created_evals}건`
              + (r.errors?.length ? ` | 오류 ${r.errors.length}건: ${r.errors.slice(0,3).join(" / ")}` : ""),
      });
      await loadWorkers();
    } catch (err) {
      setUploadStatus({ type: "err", msg: `실패: ${err.message}` });
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
      setTimeout(() => setUploadStatus(null), 6000);
    }
  };

  /* ── 연도 토글 ─────────────────────────── */
  const toggleYear = (yr) =>
    setOpenYears((p) => { const n = new Set(p); n.has(yr) ? n.delete(yr) : n.add(yr); return n; });

  /* ════════════════════════════════════════
     드로어
  ════════════════════════════════════════ */
  const renderDrawer = () => {
    if (!drawerWorker) return null;
    const evalsByYear  = groupEvalsByYear(drawerWorker.evals);
    const scoresByYear = drawerWorker.scores_by_year || {};
    const drawerYears  = Array.from(
      new Set([THIS_YEAR, ...Object.keys(evalsByYear).map(Number)])
    ).sort((a, b) => b - a);

    return (
      <>
        <div
          className={[styles.drawerOverlay, drawerOpen ? styles.drawerOverlayActive : ""].join(" ")}
          onClick={closeDrawer}
        />
        <div className={[styles.drawer, drawerOpen ? styles.drawerActive : ""].join(" ")}>
          <div className={styles.drawerHeader}>
            <div>
              <div className={styles.drawerName}>{drawerWorker.name}</div>
              <div className={styles.drawerMeta}>
                {drawerWorker.site}
                {drawerWorker.field && ` · ${drawerWorker.field}`}
                <span className={drawerWorker.working === "Y" ? styles.workBadgeOn : styles.workBadgeOff}>
                  {drawerWorker.working === "Y" ? "근무중" : "휴무중"}
                </span>
              </div>
            </div>
            <div className={styles.drawerHeaderBtns}>
              <button type="button" className={styles.btnXs}
                onClick={() => openModal(drawerWorker.id)}>편집</button>
              <button type="button" className={[styles.btnXs, styles.btnDanger].join(" ")}
                onClick={() => deleteWorker(drawerWorker.id)}>삭제</button>
              <button type="button" className={styles.drawerClose} onClick={closeDrawer}>✕</button>
            </div>
          </div>

          <div className={styles.drawerBody}>
            {drawerYears.map((yr) => {
              const evals   = evalsByYear[yr] || [];
              const score   = scoresByYear[yr] ?? 100;
              const sc      = scoreColor(score);
              const isOpen  = openYears.has(yr);
              const isAddingThisYear =
                editingKey === "new" &&
                new Date(evalDraft.eval_date).getFullYear() === yr;

              return (
                <div key={yr} className={styles.yearSection}>
                  <div className={styles.yearHeader} onClick={() => toggleYear(yr)}>
                    <div className={styles.yearLeft}>
                      <span className={styles.yearToggleIcon}>{isOpen ? "▾" : "▸"}</span>
                      <span className={styles.yearLabel}>{yr}년</span>
                      <span className={styles.yearScore}
                        style={{ backgroundColor: sc.bg, borderColor: sc.border, color: sc.text }}>
                        {score}점
                      </span>
                      {evals.length > 0 && (
                        <span className={styles.evalCount}>{evals.length}건</span>
                      )}
                    </div>
                    {isOpen && editingKey === null && (
                      <button type="button" className={styles.btnAddEval}
                        onClick={(e) => { e.stopPropagation(); startAddEval(yr); }}>
                        + 평가 추가
                      </button>
                    )}
                  </div>

                  {isOpen && (
                    <div className={styles.yearBody}>
                      {isAddingThisYear && (
                        <EvalEditCard
                          draft={evalDraft}
                          setDraft={setEvalDraft}
                          onSave={() => saveEval("new")}
                          onCancel={cancelEditEval}
                        />
                      )}

                      {evals.length === 0 && !isAddingThisYear && (
                        <div className={styles.evalNone}>평가 내용 없음</div>
                      )}

                      {evals.length > 0 && (
                        <table className={styles.evalTable}>
                          <colgroup>
                            <col style={{ width: "30px" }} />
                            <col style={{ width: "84px" }} />
                            <col style={{ width: "64px" }} />
                            <col style={{ width: "56px" }} />
                            <col />
                            <col style={{ width: "62px" }} />
                          </colgroup>
                          <thead>
                            <tr>
                              <th>연번</th>
                              <th>평가일자</th>
                              <th>평가자/<br />제보자</th>
                              <th>점수<br />변화</th>
                              <th>평가내용</th>
                              <th>관리</th>
                            </tr>
                          </thead>
                          <tbody>
                            {evals.map((ev, idx) => {
                              const isEditing = editingKey === ev.id;
                              const cl = changeLabel(ev.score_change || 0);

                              return isEditing ? (
                                <tr key={ev.id} className={styles.editingTr}>
                                  <td>{idx + 1}</td>
                                  <td>
                                    <input type="date" className={styles.inlineInput}
                                      value={evalDraft.eval_date}
                                      onChange={(e) => setEvalDraft((p) => ({ ...p, eval_date: e.target.value }))} />
                                  </td>
                                  <td>
                                    <input type="text" className={styles.inlineInput}
                                      value={evalDraft.evaluator} placeholder="평가자"
                                      onChange={(e) => setEvalDraft((p) => ({ ...p, evaluator: e.target.value }))} />
                                  </td>
                                  <td>
                                    <div className={styles.inlineScoreWrap}>
                                      <div className={styles.scoreToggle}>
                                        <button type="button"
                                          className={[styles.toggleBtn, evalDraft.score_type === "plus" ? styles.togglePlus : ""].join(" ")}
                                          onClick={() => setEvalDraft((p) => ({ ...p, score_type: "plus" }))}>＋</button>
                                        <button type="button"
                                          className={[styles.toggleBtn, evalDraft.score_type === "minus" ? styles.toggleMinus : ""].join(" ")}
                                          onClick={() => setEvalDraft((p) => ({ ...p, score_type: "minus" }))}>－</button>
                                      </div>
                                      <input type="number" className={styles.inlineScoreInput}
                                        value={evalDraft.score_value} min={0} max={100}
                                        onChange={(e) => setEvalDraft((p) => ({ ...p, score_value: e.target.value }))} />
                                    </div>
                                  </td>
                                  <td>
                                    <textarea className={styles.inlineTextarea}
                                      value={evalDraft.content} rows={2} placeholder="평가 내용..."
                                      onChange={(e) => setEvalDraft((p) => ({ ...p, content: e.target.value }))} />
                                  </td>
                                  <td>
                                    <div className={styles.evalRowBtns}>
                                      <button type="button" className={[styles.btnXs, styles.btnBlue].join(" ")}
                                        onClick={() => saveEval(ev.id)}>저장</button>
                                      <button type="button" className={styles.btnXs}
                                        onClick={cancelEditEval}>취소</button>
                                    </div>
                                  </td>
                                </tr>
                              ) : (
                                <tr key={ev.id} className={styles.evalTr}>
                                  <td>{idx + 1}</td>
                                  <td>{ev.eval_date}</td>
                                  <td>{ev.evaluator || "-"}</td>
                                  <td style={{ color: cl.color, fontWeight: 800 }}>{cl.text}</td>
                                  <td className={styles.evalContentCell}>{ev.content || "-"}</td>
                                  <td>
                                    <div className={styles.evalRowBtns}>
                                      <button type="button" className={styles.btnXs}
                                        onClick={() => { startEditEval(ev); setOpenYears((p) => new Set([...p, yr])); }}>
                                        수정
                                      </button>
                                      <button type="button" className={[styles.btnXs, styles.btnDanger].join(" ")}
                                        onClick={() => deleteEval(ev.id)}>
                                        삭제
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </>
    );
  };

  /* ════════════════════════════════════════
     메인 렌더
  ════════════════════════════════════════ */
  const FIXED_COLS = 8; // 연번~연락처

  return (
    <div className={styles.wrap}>
      <div className={styles.header}>
        <h2 className={styles.title}>계약직 인사평가</h2>
        <div className={styles.headerActions}>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls"
            onChange={handleExcelUpload} style={{ display: "none" }} />
          <button type="button" className={styles.btnUpload}
            onClick={() => fileInputRef.current?.click()}>📥 엑셀 업로드</button>
          <button type="button" className={styles.btnAdd}
            onClick={() => openModal()}>+ 직원 추가</button>
        </div>
      </div>

      {uploadStatus && (
        <div className={[styles.uploadBar,
          uploadStatus.type === "ok"  ? styles.uploadOk  : "",
          uploadStatus.type === "err" ? styles.uploadErr : ""].join(" ")}>
          {uploadStatus.msg}
        </div>
      )}

      <div className={styles.toolbar}>
        <div className={styles.searchWrap}>
          <span className={styles.searchIcon}>🔍</span>
          <input type="text" className={styles.searchInput}
            placeholder="이름, 근무지, 분야 검색..."
            value={searchQ} onChange={(e) => setSearchQ(e.target.value)} />
        </div>
        <span className={styles.count}>
          {loading ? "조회 중..." : `총 ${workers.length}명`}
        </span>
        <span className={styles.hint}>💡 행을 더블클릭하면 평가 내용을 볼 수 있어요</span>
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <colgroup>
            <col style={{ width: "46px" }} />   {/* 연번 */}
            <col style={{ width: "80px" }} />   {/* 이름 */}
            <col style={{ width: "90px" }} />   {/* 근무지 */}
            <col style={{ width: "80px" }} />   {/* 분야 */}
            <col style={{ width: "90px" }} />   {/* 입사일 */}
            <col style={{ width: "90px" }} />   {/* 퇴사일 */}
            <col style={{ width: "90px" }} />   {/* 생년월일 */}
            <col style={{ width: "110px" }} />  {/* 연락처 */}
            {years.map((y) => <col key={y} style={{ width: "80px" }} />)}
          </colgroup>
          <thead>
            <tr>
              <th>연번</th>
              <th>이름</th>
              <th>근무지</th>
              <th>분야</th>
              <th>입사일</th>
              <th>퇴사일</th>
              <th>생년월일</th>
              <th>연락처</th>
              {years.map((y) => (
                <th key={y} className={y === THIS_YEAR ? styles.thCurrentYear : ""}>
                  {y}년
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {workers.length === 0 ? (
              <tr>
                <td colSpan={FIXED_COLS + years.length} className={styles.empty}>
                  {loading ? "불러오는 중..." : "등록된 직원이 없습니다."}
                </td>
              </tr>
            ) : (
              workers.map((w, i) => {
                const isSelected = drawerOpen && drawerWorker?.id === w.id;
                return (
                  <tr key={w.id}
                    className={[styles.dataRow, isSelected ? styles.rowSelected : ""].join(" ")}
                    onClick={() => handleRowClick(w)}>
                    <td>{i + 1}</td>
                    <td className={styles.tdName}>{w.name}</td>
                    <td>{w.site}</td>
                    <td>{w.field || "-"}</td>
                    <td>{w.hire_date || "-"}</td>
                    <td>{w.resign_date || "-"}</td>
                    <td>{w.birth || "-"}</td>
                    <td>{w.phone || "-"}</td>
                    {years.map((y) => {
                      const score    = (w.scores_by_year || {})[y];
                      const hasScore = score !== undefined;
                      const sc       = hasScore ? scoreColor(score) : null;
                      return (
                        <td key={y}>
                          {hasScore ? (
                            <span className={styles.scoreChip}
                              style={{ backgroundColor: sc.bg, borderColor: sc.border, color: sc.text }}>
                              {score}
                            </span>
                          ) : (
                            <span className={styles.scoreEmpty}>-</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {renderDrawer()}

      {/* 직원 모달 */}
      {modalOpen && (
        <div className={styles.modalBg} {...modalBackdrop}>
          <div className={styles.modal}>
            <h3 className={styles.modalTitle}>
              {modalEditId ? "직원 정보 편집" : "직원 추가"}
            </h3>

            <div className={styles.modalRow}>
              <div className={styles.field}>
                <label>이름 *</label>
                <input type="text" value={form.name} placeholder="홍길동"
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
              </div>
              <div className={styles.field}>
                <label>성별</label>
                <select value={form.gender}
                  onChange={(e) => setForm((p) => ({ ...p, gender: e.target.value }))}>
                  <option value="남">남</option><option value="여">여</option>
                </select>
              </div>
            </div>

            <div className={styles.modalRow}>
              <div className={styles.field}>
                <label>생년월일</label>
                <input type="date" value={form.birth || ""}
                  onChange={(e) => setForm((p) => ({ ...p, birth: e.target.value }))} />
              </div>
              <div className={styles.field}>
                <label>연락처</label>
                <input type="text" value={form.phone} placeholder="010-0000-0000" maxLength={13}
                  onChange={(e) => setForm((p) => ({ ...p, phone: formatPhone(e.target.value) }))} />
              </div>
            </div>

            <div className={styles.modalRow}>
              <div className={styles.field}>
                <label>근무지 *</label>
                <input type="text" value={form.site} placeholder="가산점"
                  onChange={(e) => setForm((p) => ({ ...p, site: e.target.value }))} />
              </div>
              <div className={styles.field}>
                <label>분야</label>
                <input type="text" value={form.field} placeholder="미화 / 경비 / 주차 / 안내"
                  onChange={(e) => setForm((p) => ({ ...p, field: e.target.value }))} />
              </div>
            </div>

            <div className={styles.modalRow}>
              <div className={styles.field}>
                <label>입사일</label>
                <input type="date" value={form.hire_date || ""}
                  onChange={(e) => setForm((p) => ({ ...p, hire_date: e.target.value }))} />
              </div>
              <div className={styles.field}>
                <label>퇴사일</label>
                <input type="date" value={form.resign_date || ""}
                  onChange={(e) => setForm((p) => ({ ...p, resign_date: e.target.value }))} />
              </div>
            </div>

            <div className={styles.modalRow}>
              <div className={styles.field}>
                <label>근무여부</label>
                <select value={form.working}
                  onChange={(e) => setForm((p) => ({ ...p, working: e.target.value }))}>
                  <option value="Y">근무 중</option><option value="N">휴무 중</option>
                </select>
              </div>
            </div>

            <div className={styles.modalActions}>
              <button type="button" className={styles.btnCancel}
                onClick={() => setModalOpen(false)}>취소</button>
              <button type="button" className={styles.btnSavePrimary}
                onClick={saveModal}>저장</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── 새 평가 입력 카드 ───────────────────── */
function EvalEditCard({ draft, setDraft, onSave, onCancel }) {
  const upd = (k, v) => setDraft((p) => ({ ...p, [k]: v }));
  return (
    <div className={styles.evalEditCard}>
      <div className={styles.evalEditRow}>
        <div className={styles.evalEditField}>
          <label>평가일자</label>
          <input type="date" value={draft.eval_date}
            onChange={(e) => upd("eval_date", e.target.value)} />
        </div>
        <div className={styles.evalEditField}>
          <label>평가자 / 제보자</label>
          <input type="text" value={draft.evaluator} placeholder="홍팀장"
            onChange={(e) => upd("evaluator", e.target.value)} />
        </div>
        <div className={styles.evalEditField} style={{ maxWidth: "160px" }}>
          <label>구분 및 점수</label>
          <div className={styles.scoreRow}>
            <div className={styles.scoreToggle}>
              <button type="button"
                className={[styles.toggleBtn, draft.score_type === "plus" ? styles.togglePlus : ""].join(" ")}
                onClick={() => upd("score_type", "plus")}>＋ 가점</button>
              <button type="button"
                className={[styles.toggleBtn, draft.score_type === "minus" ? styles.toggleMinus : ""].join(" ")}
                onClick={() => upd("score_type", "minus")}>－ 감점</button>
            </div>
            <input type="number" className={styles.scoreInput}
              value={draft.score_value} min={0} max={100} placeholder="0"
              onChange={(e) => upd("score_value", e.target.value)} />
            <span className={styles.scoreUnit}>점</span>
          </div>
        </div>
      </div>
      <textarea className={styles.evalTextarea}
        value={draft.content} rows={2} placeholder="평가 내용을 입력하세요..."
        onChange={(e) => upd("content", e.target.value)} />
      <div className={styles.evalEditBtns}>
        <button type="button" className={[styles.btnXs, styles.btnBlue].join(" ")} onClick={onSave}>저장</button>
        <button type="button" className={styles.btnXs} onClick={onCancel}>취소</button>
      </div>
    </div>
  );
}